import type { NostrEvent } from '@nostrify/nostrify';

/** Known payto types rendered with friendly labels (NIP-A3). */
export const PAYTO_TYPES = [
  'lightning',
  'bitcoin',
  'monero',
  'litecoin',
  'bitcoin-cash',
  'nano',
  'ethereum',
  'solana',
  'zcash',
  'dogecoin',
] as const;

export type PaytoType = (typeof PAYTO_TYPES)[number] | (string & {});

export interface PaytoEntry {
  type: string;
  address: string;
}

/** Coins whose tips are private (not publicly countable). */
export const PRIVATE_COINS = new Set(['monero', 'nano', 'zcash', 'litecoin', 'bitcoin-cash']);

/** Friendly display label for a payto type. */
export function paytoLabel(type: string): string {
  switch (type) {
    case 'lightning':
      return 'Lightning';
    case 'bitcoin':
      return 'Bitcoin';
    case 'monero':
      return 'Monero';
    case 'litecoin':
      return 'Litecoin';
    case 'bitcoin-cash':
      return 'Bitcoin Cash';
    case 'nano':
      return 'Nano';
    case 'ethereum':
      return 'Ethereum';
    case 'solana':
      return 'Solana';
    case 'zcash':
      return 'Zcash';
    case 'dogecoin':
      return 'Dogecoin';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
}

/** URI scheme prefix per coin, used for wallet deep links. */
function schemeFor(type: string): string {
  switch (type) {
    case 'lightning':
      return 'lightning:';
    case 'bitcoin':
      return 'bitcoin:';
    case 'monero':
      return 'monero:';
    case 'litecoin':
      return 'litecoin:';
    case 'bitcoin-cash':
      return 'bitcoincash:';
    case 'nano':
      return 'nano:';
    case 'ethereum':
      return 'ethereum:';
    case 'solana':
      return 'solana:';
    case 'zcash':
      return 'zcash:';
    case 'dogecoin':
      return 'dogecoin:';
    default:
      return `payto://${type}/`;
  }
}

/** Parses `payto` tags from a kind 10133 event. */
export function parsePayto(event: NostrEvent | undefined): PaytoEntry[] {
  if (!event || event.kind !== 10133) return [];
  return event.tags
    .filter(([name]) => name === 'payto')
    .map(([, type, address]) => ({ type: type?.toLowerCase() ?? '', address: address ?? '' }))
    .filter((e) => e.type.length > 0 && e.address.length > 0);
}

/** Builds a wallet URI for a payto entry (per NIP-A3 / RFC-8905 fallback). */
export function paytoUri(type: string, address: string): string {
  const scheme = schemeFor(type);
  if (scheme.endsWith('//')) return `${scheme}${address}`;
  return `${scheme}${address}`;
}

/** Truncates a long address for compact display. */
export function truncateAddr(address: string): string {
  if (address.length <= 16) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/** Validates a payto type string. */
export function isValidPaytoType(type: string): boolean {
  return /^[a-z0-9-]+$/.test(type);
}

/** Validates a payto address string (non-empty, no spaces). */
export function isValidPaytoAddress(address: string): boolean {
  return address.trim().length > 0 && !/\s/.test(address);
}
